import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EventOperators } from '../../components/EventOperators.js';
import { ApiError, api } from '../../lib/api.js';
import { logout, switchOrganization } from '../../lib/session.js';
import { useSession } from '../../lib/useSession.js';

/**
 * /admin — administração.
 *
 * O login que vivia aqui dentro saiu: quem não tem sessão é mandado para
 * `/entrar` pelo `RequireAuth`, como em qualquer outra tela protegida. Era
 * esse formulário embutido que ficava preso depois de um login bem-sucedido.
 *
 * Nesta etapa a tela é o mínimo para operar; a versão completa (programação de
 * rodadas, histórico, resumo, exportação, tema) é a etapa seguinte.
 */

interface EventSummary {
  id: string;
  name: string;
  venue: string | null;
  hasOpenGame: boolean;
}

export function Admin() {
  const session = useSession();
  const navigate = useNavigate();

  const [events, setEvents] = useState<EventSummary[]>([]);
  const [name, setName] = useState('');
  const [openOperators, setOpenOperators] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    if (!session.activeOrganizationId) {
      setEvents([]);
      return;
    }

    void api<EventSummary[]>('/api/events')
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [session.activeOrganizationId]);

  useEffect(reload, [reload]);

  return (
    <main className="shell">
      <header className="shell-head">
        <div>
          <h1>Administração</h1>
          <p className="hint-text">
            {session.user?.name}
            {session.activeRole ? ` · ${session.activeRole.toLowerCase()}` : ''}
          </p>
        </div>
        <div className="row">
          <Link className="btn" to="/admin/membros">
            Membros
          </Link>
          <Link className="btn" to="/historico">
            Histórico
          </Link>
          <Link className="btn" to="/inicio">
            Meus jogos
          </Link>
          <button
            type="button"
            className="btn"
            onClick={() => void logout().then(() => navigate('/', { replace: true }))}
          >
            Sair
          </button>
        </div>
      </header>

      {session.memberships.length > 1 ? (
        <label className="field">
          <span>Organização</span>
          <select
            value={session.activeOrganizationId ?? ''}
            onChange={(event) => void switchOrganization(event.target.value)}
          >
            <option value="" disabled>
              Escolha uma organização
            </option>
            {session.memberships.map((membership) => (
              <option key={membership.organizationId} value={membership.organizationId}>
                {membership.organizationName} · {membership.role}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <h2>Eventos</h2>

      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          if (!name.trim()) return;

          void api<EventSummary>('/api/events', { method: 'POST', body: { name: name.trim() } })
            .then(() => {
              setName('');
              setError(null);
              reload();
            })
            .catch((err: unknown) =>
              setError(err instanceof ApiError ? err.message : 'Não foi possível criar.'),
            );
        }}
      >
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Nome do evento"
          aria-label="Nome do evento"
        />
        <button type="submit" className="btn primary">
          Criar
        </button>
      </form>

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="event-list">
        {events.map((event) => (
          <div key={event.id} className="event-card">
            <div className="event-row">
              <div>
                <strong>{event.name}</strong>
                {event.venue ? <small>{event.venue}</small> : null}
              </div>
              <div className="person-actions">
                <button
                  type="button"
                  className="btn"
                  aria-expanded={openOperators === event.id}
                  onClick={() =>
                    setOpenOperators((current) => (current === event.id ? null : event.id))
                  }
                >
                  Operadores
                </button>
                <Link className="btn" to={`/controle?eventId=${event.id}`}>
                  Abrir controle
                </Link>
              </div>
            </div>

            {openOperators === event.id ? <EventOperators eventId={event.id} /> : null}
          </div>
        ))}
        {events.length === 0 ? <p className="hint-text">Nenhum evento ainda.</p> : null}
      </div>

      {session.user?.isPlatformAdmin ? (
        <nav className="shell-links">
          <Link className="btn" to="/admin/plataforma">
            Plataforma
          </Link>
        </nav>
      ) : null}
    </main>
  );
}
